import type { ImageAspectRatio } from '@shared/types/image.ts';
import { type IntentResult, isContextIntent, isLayoutIntent } from '@shared/types/types.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type Columns = 'one-two' | 'two-three';

export type TileLayout = 'default' | 'fashion';

export const ALLOWED_LAYOUT_PARAMS = ['gp', 'gl', 'gq'] as const;
export type LayoutParam = (typeof ALLOWED_LAYOUT_PARAMS)[number];

export type BracketLayout = {
    imageAspectRatio: ImageAspectRatio;
    layoutParam: string;
    columns: Columns;
    tileLayout: TileLayout;
};

export const FASHION: BracketLayout = {
    imageAspectRatio: 'portrait',
    layoutParam: 'gp',
    columns: 'two-three',
    tileLayout: 'fashion',
};

export const FASHION_GALLERY_QUAD: BracketLayout = {
    ...FASHION,
    imageAspectRatio: 'quad',
    layoutParam: 'gq',
};

export const GALLERY_PORTRAIT: BracketLayout = {
    imageAspectRatio: 'portrait',
    layoutParam: 'gp',
    columns: 'two-three',
    tileLayout: 'default',
};

export const GALLERY_LANDSCAPE: BracketLayout = {
    imageAspectRatio: 'landscape',
    layoutParam: 'gl',
    columns: 'one-two',
    tileLayout: 'default',
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const GALLERY_LANDSCAPE_TWO_COLUMN: BracketLayout = {
    ...GALLERY_LANDSCAPE,
    columns: 'two-three',
    imageAspectRatio: 'landscape-two-column',
};

export const GALLERY_QUAD: BracketLayout = {
    imageAspectRatio: 'quad',
    layoutParam: 'gq',
    columns: 'two-three',
    tileLayout: 'default',
};

export function getLayout(intents: IntentResult[], layoutQueryParam?: LayoutParam, onex?: OnEx): BracketLayout {
    const resolved = resolveLayout(intents, layoutQueryParam);
    return applyExperimentOverride(resolved, onex);
}

function resolveLayout(intents: IntentResult[], layoutQueryParam?: LayoutParam): BracketLayout {
    const contextIntent = intents?.find(isContextIntent);
    if (isFashionAssortment(contextIntent?.assortment)) {
        if (isSchuhe(contextIntent?.assortment)) {
            return FASHION_GALLERY_QUAD;
        }
        return FASHION;
    }

    if (layoutQueryParam) {
        return getLayoutForParam(layoutQueryParam);
    }

    const layoutIntent = intents?.find(isLayoutIntent);
    const layout = layoutIntent?.name ?? 'GALLERY_PORTRAIT';
    switch (layout) {
        case 'GALLERY_PORTRAIT':
            return GALLERY_PORTRAIT;
        case 'GALLERY_LANDSCAPE':
            return GALLERY_LANDSCAPE;
        case 'GALLERY_QUAD':
            return GALLERY_QUAD;
    }
}

/**
 *
 *
 *
 *
 *
 *
 */
function applyExperimentOverride(layout: BracketLayout, onex?: OnEx): BracketLayout {
    if (layout === GALLERY_LANDSCAPE && onex?.isExperimentGroup('GALLERY_LANDSCAPE_TWO_COLUMN', 'E3822B')) {
        return GALLERY_LANDSCAPE_TWO_COLUMN;
    }
    return layout;
}

export function getLayoutForParam(layoutParam?: LayoutParam): BracketLayout {
    switch (layoutParam) {
        case 'gp':
            return GALLERY_PORTRAIT;
        case 'gl':
            return GALLERY_LANDSCAPE;
        case 'gq':
            return GALLERY_QUAD;
        default:
            return GALLERY_PORTRAIT;
    }
}

/**
 *
 */
const FASHION_ASSORTMENTS = ['Bekleidung', 'Schuhe', 'Wäsche'] as const;
const FASHION_ASSORTMENTS_SET = new Set(FASHION_ASSORTMENTS.map((item) => item.toLowerCase()));

function isFashionAssortment(assortment?: string): boolean {
    return !!(assortment && FASHION_ASSORTMENTS_SET.has(assortment.toLowerCase()));
}

function isSchuhe(assortment: string | undefined): boolean {
    return !!assortment && assortment.toLowerCase() === 'schuhe';
}

export function isLayoutParam(value: string | undefined): value is LayoutParam {
    return ALLOWED_LAYOUT_PARAMS.some((p) => p === value);
}
